/**
 * The billing profile a certified invoice is addressed to — a citizen's own
 * (Citizen › Billing), checked before a checkout opens and snapshotted on the
 * payment. A company's lives in `tenant_settings` and is checked against the
 * same tax-id rule.
 *
 * The country is the billing country: it decides the VAT (Portugal 23%, the
 * rest per the matrix the server reads). The tax id is optional for a person —
 * given, it must be well formed for that country.
 */
import { z } from 'zod';

import { countryCodeSchema } from '../validators';
import { isWellFormedTaxId, taxIdSchema } from './tax-id';

export const BILLING_NAME_MAX_LENGTH = 200;
export const BILLING_ADDRESS_MAX_LENGTH = 200;
export const BILLING_POSTAL_CODE_MAX_LENGTH = 20;
export const BILLING_CITY_MAX_LENGTH = 100;

export const billingProfileSchema = z
  .strictObject({
    legalName: z
      .string()
      .trim()
      .min(1, 'billing.profile.validation.legalNameRequired')
      .max(BILLING_NAME_MAX_LENGTH, 'billing.profile.validation.tooLong'),
    taxId: taxIdSchema.nullable(),
    country: z
      .string()
      .min(1, 'billing.profile.validation.countryRequired')
      .pipe(countryCodeSchema),
    addressLine1: z
      .string()
      .trim()
      .min(1, 'billing.profile.validation.addressRequired')
      .max(BILLING_ADDRESS_MAX_LENGTH, 'billing.profile.validation.tooLong'),
    addressLine2: z
      .string()
      .trim()
      .max(BILLING_ADDRESS_MAX_LENGTH, 'billing.profile.validation.tooLong')
      .nullable(),
    postalCode: z
      .string()
      .trim()
      .min(1, 'billing.profile.validation.postalCodeRequired')
      .max(BILLING_POSTAL_CODE_MAX_LENGTH, 'billing.profile.validation.tooLong'),
    city: z
      .string()
      .trim()
      .min(1, 'billing.profile.validation.cityRequired')
      .max(BILLING_CITY_MAX_LENGTH, 'billing.profile.validation.tooLong'),
  })
  .superRefine((profile, ctx) => {
    if (profile.taxId && !isWellFormedTaxId(profile.country, profile.taxId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['taxId'],
        message: 'billing.profile.validation.taxIdInvalid',
      });
    }
  });

export type BillingProfileInput = z.infer<typeof billingProfileSchema>;
